<script setup>
import { computed } from "vue";
import ArgonSearcher from "@/components/searcher/ArgonSearcher.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonTableSettings from "@/components/table/ArgonTableSettings.vue";
import { argonTableStore } from "@/_store/argonTableStore";

const tableStore = argonTableStore();

const emit = defineEmits(["filterChanged", "newItem", "onReload", "pageChanged", "eventAction"]);

const props = defineProps({
  fieldFilters: {
    type: Array,
    default() {
      return [];
    },
  },
  defaultFilters: {
    type: Array,
    default() {
      return [];
    },
  },
  total: {
    type: Number,
    default: 0,
  },
  showFavorites: {
    type: Boolean,
    default: true,
  },
  showConfigOption: {
    type: Boolean,
    default: false,
  },
  showNewButton: {
    type: Boolean,
    default: false,
  },
  moreActions: {
    type: Array,
    default() {
      return [];
    },
  },
});

function filterChanged(changes) {
  emit("filterChanged", changes);
}

function onEventAction(action) {
  emit("eventAction", action);
}

function onReload() {
  emit("onReload");
}

function newItem() {
  emit("newItem");
}

function changePage(newPage) {
  tableStore.pagination.page = newPage;
  emit("pageChanged", newPage);
}

const previousPage = computed(() => {
  if (tableStore.pagination.page === 1) {
    return Math.max(1, Math.ceil(props.total / tableStore.pagination.limit));
  }
  return tableStore.pagination.page - 1;
});

const nextPage = computed(() => {
  const maxPages = Math.max(1, Math.ceil(props.total / tableStore.pagination.limit));
  if (tableStore.pagination.page === maxPages) {
    return 1;
  }
  return tableStore.pagination.page + 1;
});

const paginationDescription = computed(() => {
  if (props.total === 0) return "0-0";
  const startPage = (tableStore.pagination.page - 1) * tableStore.pagination.limit + 1;
  const endPage = startPage + tableStore.pagination.limit - 1;
  return `${startPage}-${endPage > props.total ? props.total : endPage}`;
});

const totalDescription = computed(() => {
  return props.total >= 2000 ? `+${props.total}` : props.total;
});
</script>

<template>
  <div class="row m-0 pt-1">
    <div class="col-md-auto d-flex ps-3 align-items-center">
      <argon-button v-if="props.showNewButton" class="me-2 btn-xs" color="primary" @click="newItem">
        <i class="fa fa-plus me-sm-2"></i>
        Nuevo
      </argon-button>
      <argon-table-settings
        v-if="props.showConfigOption"
        :show-config-option="props.showConfigOption"
        :show-importar="false"
        :more-actions="props.moreActions"
        @event-action="onEventAction"
      />
    </div>

    <div class="col d-flex justify-content-center pe-5 ps-5">
      <argon-searcher
        size="sm"
        icon="fa fa-search"
        class="argon-searcher w-50"
        placeholder="Buscar..."
        :field-filters="props.fieldFilters"
        :default-filters="props.defaultFilters"
        :show-favorites="props.showFavorites"
        @filter-changed="filterChanged"
      />
    </div>

    <div class="col-md-auto">
      <nav aria-label="paginacion" class="d-flex justify-content-end align-items-center">
        <ul class="pagination m-0 pe-1">
          <li class="page-item">
            <a v-tooltip="'Refrescar'" class="page-link" @click="onReload">&#8634;</a>
          </li>
        </ul>
        <ul class="m-0 ps-1 pe-1 text-xs">
          <span class="page">{{ paginationDescription }} / {{ totalDescription }}</span>
        </ul>
        <ul class="pagination justify-content-end m-0">
          <li class="page-item">
            <a class="page-link" @click="changePage(previousPage)">&lt;</a>
          </li>
          <li class="page-item">
            <a class="page-link" @click="changePage(nextPage)">&gt;</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<style scoped>
.argon-searcher {
  width: 100%;
  max-width: 1140px;
  min-width: 200px;
}
</style>
